import { useState } from "react";
import BoldLayout from "../bold-layout";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import type { ConsentType } from "../../types";
import type { ApiPage, Brandkit, FormBrand } from "../../types/api.types";

interface StepConsentProps {
	brandkit: Brandkit;
	brand: FormBrand;
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	pageConfig: ApiPage;
	totalSteps: number;
	currentStepIndex: number;
	isDarkMode?: boolean;
}

export default function StepConsent({
	brandkit,
	brand,
	showBackButton,
	onBack,
	stepKey,
	pageConfig,
	totalSteps,
	currentStepIndex,
	isDarkMode,
}: StepConsentProps) {
	const {
		data,
		setConsent,
		nextStep,
		canProceed,
		updateConsentAndComplete,
		isSubmitting,
		submitError,
	} = useFeedbackFlow();
	const [error, setError] = useState<string | null>(null);

	const handleSelect = (consent: ConsentType) => {
		setConsent(consent);
		setError(null); // Clear error when consent changes
	};

	const handleContinue = async () => {
		if (!canProceed()) return;

		try {
			setError(null);
			// Save consent and mark submission as completed
			await updateConsentAndComplete();
			nextStep(); // Go directly to thank you page (uploading step removed)
		} catch (err) {
			setError(
				submitError ||
					"Failed to submit your feedback. Please try again.",
			);
		}
	};

	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	return (
		<BoldLayout
			showBranding={brand?.branding !== "disabled"}
			brandName={brand.name}
			brandLogo={brandLogo}
			brandkit={brandkit}
			stepKey={stepKey}
			showBackButton={showBackButton}
			onBack={onBack}
			title={pageConfig.attributes.title}
			description={pageConfig.attributes.description}
			progress={{ current: currentStepIndex + 1, total: totalSteps }}
			continueLabel={isSubmitting ? "Submitting..." : "Submit"}
			onContinue={handleContinue}
			continueDisabled={!canProceed() || isSubmitting}
			isDarkMode={isDarkMode}
		>
			<div className="space-y-3">
				<button
					type="button"
					onClick={() => handleSelect("public")}
					className={`w-full p-5 rounded-xl border text-left transition-all shadow-sm ${
						data.consent === "public"
							? `${isDarkMode ? "border-gray-200 bg-gray-900" : "border-gray-800 bg-white"}`
							: `${isDarkMode ? "border-gray-700 bg-gray-950" : "border-gray-100 bg-white hover:border-gray-300"}`
					}`}
					style={{
						fontFamily: "var(--font-body)",
						fontWeight: "var(--font-body-weight)",
					}}
				>
					<div className="flex items-center gap-4">
						<div
							className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
								data.consent === "public"
									? `${isDarkMode ? "border-gray-300" : "border-gray-800"}`
									: `${isDarkMode ? "border-gray-500" : "border-gray-300"}`
							}`}
						>
							{data.consent === "public" ? (
								isDarkMode ? (
									<div className="w-2.5 h-2.5 rounded-full bg-gray-200" />
								) : (
									<div className="w-2.5 h-2.5 rounded-full bg-gray-900" />
								)
							) : isDarkMode ? (
								<div className="w-2.5 h-2.5 rounded-full bg-gray-950" />
							) : (
								<div className="w-2.5 h-2.5 rounded-full bg-white" />
							)}
						</div>
						<div>
							<p
								className={`${isDarkMode ? "text-white" : "text-t-primary"}`}
								style={{
									fontFamily: "var(--font-body)",
									fontWeight: "var(--font-body-weight)",
								}}
							>
								Share it publicly
							</p>
							<p
								className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
								style={{
									fontFamily: "var(--font-body)",
									fontWeight: "var(--font-body-weight)",
								}}
							>
								On your website, social media, and marketing
								materials
							</p>
						</div>
					</div>
				</button>

				<button
					type="button"
					onClick={() => handleSelect("anonymous")}
					className={`w-full p-5 rounded-xl border text-left transition-all shadow-sm ${
						data.consent === "anonymous"
							? `${isDarkMode ? "border-gray-200 bg-gray-900" : "border-gray-800 bg-white"}`
							: `${isDarkMode ? "border-gray-700 bg-gray-950" : "border-gray-100 bg-white hover:border-gray-300"}`
					}`}
					style={{
						fontFamily: "var(--font-body)",
						fontWeight: "var(--font-body-weight)",
					}}
				>
					<div className="flex items-center gap-4">
						<div
							className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
								data.consent === "anonymous"
									? `${isDarkMode ? "border-gray-300" : "border-gray-800"}`
									: `${isDarkMode ? "border-gray-500" : "border-gray-300"}`
							}`}
						>
							{data.consent === "anonymous" ? (
								isDarkMode ? (
									<div className="w-2.5 h-2.5 rounded-full bg-gray-200" />
								) : (
									<div className="w-2.5 h-2.5 rounded-full bg-gray-900" />
								)
							) : isDarkMode ? (
								<div className="w-2.5 h-2.5 rounded-full bg-gray-950" />
							) : (
								<div className="w-2.5 h-2.5 rounded-full bg-white" />
							)}
						</div>
						<div>
							<p
								className={`${isDarkMode ? "text-white" : "text-t-primary"}`}
							>
								Share it anonymously
							</p>
							<p
								className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
							>
								On your website, social media, and marketing
								materials
							</p>
						</div>
					</div>
				</button>

				<button
					type="button"
					onClick={() => handleSelect("private")}
					className={`w-full p-5 rounded-xl border text-left transition-all shadow-sm ${
						data.consent === "private"
							? `${isDarkMode ? "border-gray-200 bg-gray-900" : "border-gray-800 bg-white"}`
							: `${isDarkMode ? "border-gray-700 bg-gray-950" : "border-gray-100 bg-white hover:border-gray-300"}`
					}`}
					style={{
						fontFamily: "var(--font-body)",
						fontWeight: "var(--font-body-weight)",
					}}
				>
					<div className="flex items-center gap-4">
						<div
							className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
								data.consent === "private"
									? `${isDarkMode ? "border-gray-300" : "border-gray-800"}`
									: `${isDarkMode ? "border-gray-500" : "border-gray-300"}`
							}`}
						>
							{data.consent === "private" ? (
								isDarkMode ? (
									<div className="w-2.5 h-2.5 rounded-full bg-gray-200" />
								) : (
									<div className="w-2.5 h-2.5 rounded-full bg-gray-900" />
								)
							) : isDarkMode ? (
								<div className="w-2.5 h-2.5 rounded-full bg-gray-950" />
							) : (
								<div className="w-2.5 h-2.5 rounded-full bg-white" />
							)}
						</div>
						<div>
							<p
								className={`${isDarkMode ? "text-white" : "text-t-primary"}`}
							>
								Keep it private
							</p>
							<p
								className={`text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
							>
								For internal use and feedback only
							</p>
						</div>
					</div>
				</button>
			</div>

			{error && (
				<div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
					<p className="text-sm text-red-600">{error}</p>
					<button
						onClick={handleContinue}
						className="mt-2 text-sm text-red-700 underline hover:no-underline"
					>
						Try Again
					</button>
				</div>
			)}
		</BoldLayout>
	);
}
